/**
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; under version 2
 * of the License (non-upgradable).
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 *
 * Copyright (c) 2020 (original work) Open Assessment Technologies SA ;
 */

/**
 * Svelte Action to provide the dragscroll custom event
 * @example
 *   <input
 *     type='text'
 *     use:dragscrollable
 *     on:dragscroll={ (x, y) => console.log(`drag scrolled for ${x} horizontally and ${y} vertically`) }
 *  />
 *
 * @param {HTMLElement} node - the target node the action applies to
 * @returns {Object} - target's lifecycle hooks
 * @fires dragscroll
 */
export default function dragScroll(node) {
    let dragscrolling = false;
    let x, y;

    //this handler has to be invoked on nodes' mousedown
    function handleMousedown(event) {
        dragscrolling = true;
        x = event.clientX;
        y = event.clientY;

        window.addEventListener('mousemove', handleMousemove);
    }

    //this handler has to be invoked on documents' mousemove
    function handleMousemove(event) {
        if (event.buttons !== 1) {
            scrollFinished();
        }
        if (dragscrolling) {
            const dx = event.clientX - x;
            const dy = event.clientY - y;

            //update initial position with the latest value
            x = event.clientX;
            y = event.clientY;

            dispatchDragscroll(node, dx, dy);
        }
    }

    //this handler has to be invoked on documents' mouseup
    function scrollFinished() {
        dragscrolling = false;
        window.removeEventListener('mousemove', handleMousemove);
    }

    /**
     * Dispatches CustomEvent 'dragscroll' on the target
     * @param {HTMLElement} target - element which is the target of dispatched event
     * @param {Number} dx - horizontal distance been scrolled
     * @param {Number} dy - vertical distance been scrolled
     * @fires dragscroll
     */
    function dispatchDragscroll(target, dx, dy) {
        try {
            target.dispatchEvent(
                new CustomEvent('dragscroll', {
                    detail: { x, y, dx, dy }
                })
            );
        } catch (error) {
            // IE11 does not support using CustomEvent as a constructor without init
            const customEvent = document.createEvent('CustomEvent');
            customEvent.initCustomEvent('dragscroll', false, false, { x, y, dx, dy });
            target.dispatchEvent(customEvent);
        }
    }

    //attach mousedown listener to node
    node.addEventListener('mousedown', handleMousedown);

    return {
        destroy: () => {
            node.removeEventListener('mousedown', handleMousedown);
            window.removeEventListener('mousemove', handleMousemove);
        }
    };
}
